<template>
  <section class="bg-blue-400/10 py-12">
    <div class="container-wrap">
      <div class="desktop:flex desktop:items-center">
        <div class="mb-4 w-full desktop:mb-0 desktop:w-6/12 desktop:pr-8">
          <p class="text-sm font-bold text-gray-600">
            {{ tGlobal('products.section-name.next-steps') }}
          </p>
          <h2 class="text-h2 mb-6">{{ t('title', { productName }) }}</h2>
          <p class="mb-6 text-sm text-gray-600 tablet:text-base">
            {{ t('description') }}
          </p>
          <TheButton
            :id="isCN ? 'tracking_requestdemo' : undefined"
            :to="`/${$i18n.locale}/marketplace/products/${$route.params.id}/request-demo`"
            :title="tGlobal('products.book-a-demo')"
            class="mb-2 w-full tablet:mb-0 tablet:w-[180px]"
          >
            <span class="text-sm">{{ tGlobal('products.book-a-demo') }}</span>
          </TheButton>
        </div>
        <div class="w-full desktop:w-6/12 desktop:pl-8">
          <ul class="column-121">
            <li v-if="!isLogin">
              <button
                :id="isCN ? 'tracking_login' : undefined"
                :title="t('item-1-title')"
                class="element_card block w-full rounded bg-white p-3 shadow-normal shadow-gray-400 hover:shadow-blue-400/50 tablet:p-4 desktop:p-5"
                @click="handleLogin"
              >
                <p class="mb-2 text-sm font-bold leading-6 tablet:text-base">
                  {{ t('item-1-title') }}
                </p>
                <p class="mb-2 text-sm text-gray-600">
                  {{ t('item-1-description') }}
                </p>
                <div class="hidden tablet:flex tablet:justify-end">
                  <div
                    class="element_arrow flex h-8 w-8 basis-8 items-center justify-center rounded text-blue-400"
                  >
                    <IconArrow class="h-5 w-5" direction="right" />
                  </div>
                </div>
              </button>
            </li>
            <li>
              <div
                :title="t('item-2-title')"
                class="element_card block w-full cursor-pointer rounded bg-white p-3 shadow-normal shadow-gray-400 hover:shadow-blue-400/50 tablet:p-4 desktop:p-5"
                @click="handleClick"
              >
                <p class="mb-2 text-sm font-bold leading-6 tablet:text-base">
                  {{ t('item-2-title') }}
                </p>
                <p class="mb-2 text-sm text-gray-600">
                  {{ t('item-2-description') }}
                </p>
                <div class="hidden tablet:flex tablet:justify-end">
                  <TheNuxtLink
                    :id="isCN ? 'tracking_form' : undefined"
                    :to="`/${$i18n.locale}/marketplace/products/${$route.params.id}/contact-sales`"
                    :title="t('item-2-title')"
                    class="element_arrow flex h-8 w-8 basis-8 items-center justify-center rounded text-blue-400"
                  >
                    <IconArrow class="h-5 w-5" direction="right" />
                  </TheNuxtLink>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  const route = useRoute();
  const router = useRouter();
  const { t, locale } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });
  const { isCN } = useLanguage();

  const profileStore = useProfileStore();
  const productsStore = useProductsStore();
  const { isLogin } = storeToRefs(profileStore);
  const { productName } = storeToRefs(productsStore);

  const handleLogin = (): void => {
    if (import.meta.client) {
      const callbackUrl = window.location.href;
      profileStore.HANDLE_LOGIN(callbackUrl);
    }
  };

  const handleClick = (): void => {
    router.push(
      `/${locale.value}/marketplace/products/${route.params.id}/contact-sales`
    );
  };
</script>

<style lang="scss" scoped>
  .element_card:hover {
    .element_arrow {
      @apply bg-blue-400 text-white;
    }
  }
</style>

<i18n lang="json">
{
  "en-int": {
    "title": "Get started with {productName}",
    "description": "Request a demo and discover how it can transform your operations.",
    "item-1-title": "Create Free Account",
    "item-1-description": "Accelerate your digital transformation with a free account.",
    "item-2-title": "Talk to Our Experts",
    "item-2-description": "Our experts are ready to assist you in selecting the best products and packages."
  },
  "en-jp": {
    "title": "Get started with {productName}",
    "description": "Request a demo and discover how it can transform your operations.",
    "item-1-title": "Create Free Account",
    "item-1-description": "Accelerate your digital transformation with a free account.",
    "item-2-title": "Talk to Our Experts",
    "item-2-description": "Our experts are ready to assist you in selecting the best products and packages."
  },
  "en-tw": {
    "title": "Get started with {productName}",
    "description": "Request a demo and discover how it can transform your operations.",
    "item-1-title": "Create Free Account",
    "item-1-description": "Accelerate your digital transformation with a free account.",
    "item-2-title": "Talk to Our Experts",
    "item-2-description": "Our experts are ready to assist you in selecting the best products and packages."
  },
  "en-kr": {
    "title": "Get started with {productName}",
    "description": "Request a demo and discover how it can transform your operations.",
    "item-1-title": "Create Free Account",
    "item-1-description": "Accelerate your digital transformation with a free account.",
    "item-2-title": "Talk to Our Experts",
    "item-2-description": "Our experts are ready to assist you in selecting the best products and packages."
  },
  "en-americas": {
    "title": "Get started with {productName}",
    "description": "Request a demo and discover how it can transform your operations.",
    "item-1-title": "Create Free Account",
    "item-1-description": "Accelerate your digital transformation with a free account.",
    "item-2-title": "Talk to Our Experts",
    "item-2-description": "Our experts are ready to assist you in selecting the best products and packages."
  },
  "en-asia-pacific": {
    "title": "Get started with {productName}",
    "description": "Request a demo and discover how it can transform your operations.",
    "item-1-title": "Create Free Account",
    "item-1-description": "Accelerate your digital transformation with a free account.",
    "item-2-title": "Talk to Our Experts",
    "item-2-description": "Our experts are ready to assist you in selecting the best products and packages."
  },
  "en-emea": {
    "title": "Get started with {productName}",
    "description": "Request a demo and discover how it can transform your operations.",
    "item-1-title": "Create Free Account",
    "item-1-description": "Accelerate your digital transformation with a free account.",
    "item-2-title": "Talk to Our Experts",
    "item-2-description": "Our experts are ready to assist you in selecting the best products and packages."
  },
  "ja-jp": {
    "title": "{productName} を始める",
    "description": "デモをリクエストして、業務をどのように変革できるかをご確認ください。",
    "item-1-title": "無料アカウント作成",
    "item-1-description": "無料アカウントで、DXを加速させましょう。",
    "item-2-title": "専門家に相談",
    "item-2-description": "専門家が最適な製品とプラン選びをサポートします。"
  },
  "zh-cn": {
    "title": "开始使用 {productName}",
    "description": "申请演示，了解它如何改变您的业务。",
    "item-1-title": "创建免费账号",
    "item-1-description": "通过免费账户，加速您的数字化转型。",
    "item-2-title": "咨询专家",
    "item-2-description": "我们的专家随时协助您选择最合适的产品和方案。"
  },
  "zh-tw": {
    "title": "開始使用 {productName}",
    "description": "申請示範，了解它如何改變您的業務。",
    "item-1-title": "建立免費帳號",
    "item-1-description": "透過免費帳號，加速您的數位轉型。",
    "item-2-title": "諮詢專家",
    "item-2-description": "我們的專家隨時協助您選擇最合適的產品和方案。"
  }
}
</i18n>
